import type { QueryClient, QueryObserverResult } from "react-query";

import type {
  CasAddressStored,
  ContactPersonal,
  DateOfBirth,
  Gender,
  LoginAuthenticationStrategies,
  OtpChannel,
  PreparedProfileType,
  ResponseProfileType,
} from "@jti/fe-common";

export type UseProfileOptions = {
  queryClient: QueryClient;
  onLogout?: (uuid: string) => void;
};

export type UpdatePersonalDataSuccessCallback = (response: ResponseProfileType) => void | Promise<void>;
export type UpdatePersonalDetailsData = {
  firstname: string;
  lastname: string;
  date_of_birth: DateOfBirth;
  gender?: Gender;
  onSuccessOverride?: UpdatePersonalDataSuccessCallback;
};

export type UseProfileReturnType = {
  isLoadingData: boolean;
  userUuid?: string;
  data?: PreparedProfileType;
  logout: () => Promise<boolean>;
  changePassword: (newPassword: string, currentPassword: string, confirmPassword: string) => Promise<boolean>;
  updateEmail: (email: string) => Promise<{ statusCode: number }>;
  updatePhone: (
    phone: string,
    pvs?: string,
    avp?: string
  ) => Promise<{ personalInfo: ContactPersonal; addresses: CasAddressStored[] }>;
  sendOTC: (
    phone: string,
    otpChannel: OtpChannel,
    otpTemplateName?: string
  ) => Promise<{ phone_verification_uuid: string }>;
  checkPhone: (phone: string) => Promise<{ statusCode: number }>;
  updateAgeVerification: (avpUuid: string) => Promise<{ is_age_verified: boolean }>;
  updatePersonalData: (updateProfileData: UpdatePersonalDetailsData) => Promise<ResponseProfileType>;
};

export type UseLazyProfileReturnType = {
  getUserProfile: () => Promise<QueryObserverResult<PreparedProfileType, unknown>>;
};

export type UseSendOTCReturnType = {
  sendOTC: (
    phone: string,
    otpChannel: OtpChannel,
    otpTemplateName?: string
  ) => Promise<{ phone_verification_uuid: string }>;
};

export type UseSendLoginOTCReturnType = {
  sendLoginOTC: (
    phone: string,
    otpChannel: OtpChannel,
    otpTemplateName?: string,
    strategy?: LoginAuthenticationStrategies
  ) => Promise<{ phone_verification_uuid?: string }>;
};

export type UseSendEmailOTCReturnType = {
  sendEmailOTC: (email: string) => Promise<{ email_verification_uuid?: string }>;
};

export type UseCheckPhoneReturnType = {
  checkPhone: (phone: string) => Promise<{ statusCode: number }>;
};
